import { useMutation } from "@apollo/client";
import { createAjv } from "@jsonforms/core";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import { Button, Divider, LoadingSpinner } from "pepsico-ds";
import { useEffect, useMemo, useState } from "react";
import { updateSegment } from "../../../graphql/mutations/createSegment";
import JsonFormsWrapper from "../../jsonForms/jsonFormsWrapper/JsonFormsWrapper";
import {
  createPlatformSegmentJsonSchema,
  createPlatformSegmentUISchema,
} from "./utils/schemas";

export default function SegmentBasicsAccordion({
  formData,
  setFormData,
  showToast,
  refetch,
  loading,
}) {
  const [update] = useMutation(updateSegment);
  const [submitting, setSubmitting] = useState(false);
  const [originalData, setOriginalData] = useState(null);
  const ajv = useMemo(() => createAjv(), []);

  useEffect(() => {
    if (!originalData && formData && formData.id) {
      setOriginalData(formData);
    }
  }, [formData, originalData]);

  const getComparable = (obj = {}) => ({
    name: (obj?.name || "").trim(),
    status: obj?.status || "",
  });

  const isDirty =
    !!originalData &&
    JSON.stringify(getComparable(formData || {})) !==
      JSON.stringify(getComparable(originalData || {}));

  const buildPayload = () => {
    const programId = localStorage.getItem("programId") || "";
    return {
      id: formData?.id,
      name: formData?.name,
      type: formData?.type || "NATIVE",
      programId,
      population: formData?.population || 0,
      status: formData?.status,
      attributes: formData?.attributes || [],
      attributeValues: formData?.attributeValues || [],
      rules: formData?.rules ?? "",
    };
  };

  const validateForm = (payload) => {
    const validator = ajv.compile(createPlatformSegmentJsonSchema);
    const isValid = validator(payload);
    if (!isValid) {
      showToast?.("error", "Please fill all the required fields");
    }
    return isValid;
  };

  const handleCancel = () => {
    if (originalData) setFormData?.({ ...originalData });
  };

  const handleSave = async () => {
    try {
      setSubmitting(true);
      if (!isDirty) return;
      const payload = buildPayload();
      if (!validateForm(payload)) return;
      await update({ variables: { input: payload } });
      showToast?.("success", "Segment updated successfully");
      setOriginalData(payload);
      refetch?.();
    } catch (e) {
      showToast?.("error", "Failed to update segment", e?.message || "");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Accordion defaultExpanded>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <strong>Basics</strong>
      </AccordionSummary>
      <AccordionDetails>
        <Divider orientation="horizontal" className="mb-2" />
        {loading && <LoadingSpinner size={{ height: "24px", width: "24px" }} />}
        {!loading && (
          <div className="segment-details">
            <JsonFormsWrapper
              jsonschema={createPlatformSegmentJsonSchema}
              uischema={createPlatformSegmentUISchema}
              data={{ ...(formData || {}) }}
              onChange={(data) => {
                if (data && Object.keys(data).length > 1) {
                  setFormData?.(data);
                }
              }}
            />
            {isDirty && (
              <>
                <Divider orientation="horizontal" className="my-2" />
                <div className="display-flex justify-content-end align-items-center mt-3">
                  <Button
                    variant="secondary"
                    text="Cancel"
                    size="medium"
                    onClick={handleCancel}
                    className="mr-3"
                  />
                  <Button
                    variant="primary"
                    text="Save"
                    size="medium"
                    disabled={submitting || !isDirty}
                    isLoading={submitting}
                    onClick={handleSave}
                  />
                </div>
              </>
            )}
          </div>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
